import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { useT } from '@/i18n';
import { TTL_OPTIONS, VIEW_OPTIONS } from '@/lib/format';

import { ChevronDownIcon, ClockIcon } from './Icons';
import styles from './linkOptions.module.css';
import type { LinkOptionsProps } from './LinkOptions.types';

interface Segment<T extends string | number> {
  readonly value: T;
  readonly label: string;
  /** What a screen reader says when the label alone is too short ("1" → "1 view"). */
  readonly spoken?: string;
}

/**
 * A segmented control, as on macOS and iOS: segments in one track, the chosen
 * one raised. It is a radio group: Tab reaches the chosen segment, the arrow
 * keys move and choose.
 */
export function Segmented<T extends string | number>({ label, options, value, onChange }: { readonly label: string; readonly options: readonly Segment<T>[]; readonly value: T; readonly onChange: (value: T) => void }): React.JSX.Element {
  const group = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const chosen = options.findIndex((option) => option.value === value);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (index + step + options.length) % options.length;
    const option = options[next];
    if (!option) return;
    onChange(option.value);
    group.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
  };

  return (
    <div className={styles.group}>
      <p id={labelId} className={styles.groupLabel}>
        {label}
      </p>
      <div ref={group} className={styles.segmented} role="radiogroup" aria-labelledby={labelId}>
        {options.map((option, index) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={index === chosen}
            aria-label={option.spoken}
            tabIndex={index === chosen || (chosen === -1 && index === 0) ? 0 : -1}
            className={styles.segment}
            onClick={() => {
              onChange(option.value);
            }}
            onKeyDown={(event) => {
              onKeyDown(event, index);
            }}
          >
            {/* The label reserves its bold width, so choosing a segment never nudges its neighbours. */}
            <span className={styles.segmentLabel} data-label={option.label}>
              {option.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * How long a bridge lives and how many may open it, as an outlined button
 * beside Create, the same size — “1 day · 1 view ⌄”. It opens a small panel with two
 * segmented rows; a choice applies at once. Escape or a click elsewhere
 * closes it, and Escape gives the focus back to the pill.
 */
export function LinkOptions({ ttl, onTtl, views, onViews, counts }: LinkOptionsProps): React.JSX.Element {
  const t = useT();
  const [open, setOpen] = useState(false);
  // opened from the keyboard, the focus goes to the chosen segment; opened with a pointer, to the panel itself,
  // so no focus ring lights up under the mouse
  const [byKeyboard, setByKeyboard] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();

  const countLabel = (count: number): string => t(counts === 'devices' ? 'linkOptions.devices' : 'linkOptions.views', { n: count });
  const ttlLabel = TTL_OPTIONS.find((option) => option.value === ttl)?.label ?? '';

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) button.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const target = byKeyboard ? panel.current?.querySelector<HTMLElement>('[aria-checked="true"]') : panel.current;
    target?.focus({ preventScroll: true });
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') close(true);
    };
    const onPointer = (event: PointerEvent): void => {
      if (event.target instanceof Node && wrap.current && !wrap.current.contains(event.target)) close(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open, close, byKeyboard]);

  return (
    <div className={styles.wrap} ref={wrap}>
      <button
        ref={button}
        type="button"
        className={styles.pill}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? id : undefined}
        onClick={(event) => {
          if (open) close(true);
          else {
            setByKeyboard(event.detail === 0);
            setOpen(true);
          }
        }}
        data-testid="options"
      >
        <ClockIcon size={18} className={styles.pillIcon} />
        <span>
          <span className="visually-hidden">{t('linkOptions.expiresIn')} </span>
          {ttlLabel} · {countLabel(views)}
        </span>
        <ChevronDownIcon size={16} className={styles.pillChevron} />
      </button>
      {open ? (
        <div
          ref={panel}
          id={id}
          className={styles.panel}
          role="dialog"
          tabIndex={-1}
          aria-label={t('linkOptions.panel')}
          onBlur={(event) => {
            if (event.relatedTarget instanceof Node && wrap.current && !wrap.current.contains(event.relatedTarget)) close(false);
          }}
          data-testid="options-panel"
        >
          <Segmented label={t('linkOptions.expiresIn')} options={TTL_OPTIONS.map((option) => ({ value: option.value, label: option.label }))} value={ttl} onChange={onTtl} />
          <Segmented
            label={counts === 'devices' ? t('common.devices') : t('common.views')}
            options={VIEW_OPTIONS.map((count) => ({ value: count, label: String(count), spoken: countLabel(count) }))}
            value={views}
            onChange={onViews}
          />
        </div>
      ) : null}
    </div>
  );
}
